// @ts-nocheck
import { CustomPartnerData, CustomPartnerLimitResponse } from '../../../../shared/types/customPartnerLimits.types';
import type {
  AdPartner,
  AdPartnerReducer,
  SnapAppIds,
  ExtraData,
  FacebookData,
  TikTokAccount,
} from '../types/adPartnerTypes';
import { INIT_STATE } from '../reducers/AdPartnerReducer';
import { createSelector } from 'reselect';
import { LIMITED_DATA_USE } from '../components/PartnerManager/constants';
import RokuDefaults from '../components/PartnerManager/Roku/RokuDefaults';
import { isUserOnAppView } from './OrgSelectors';
const ENGAGEMENT_BUILDER_PARTNER_BLACKLIST = ['a_facebook', 'a_pinterest', 'a_roku'];
export function isAdPartnerVisible(partner: AdPartner) {
  return (
    partner.status === 'active' || // case: custom ad partner. Always show
    partner.machine_name.includes('a_custom_') || // case: tune ad network blueprint. Show if a configuration exists (entry in ad_network).
    (partner.status === 'tune' && !partner.neverConfigured)
  );
}
export function isAdPartnerPartOfAnySKAdNetwork(partner: AdPartner) {
  return Boolean(partner.sk_ad_network_id);
}
export function isAppLevelAdPartner(partner: AdPartner) {
  return !Boolean(partner.organization_id);
}
export function getState(state: any): AdPartnerReducer {
  return (state && state.ad_partner) || INIT_STATE;
}
export const getSelectedPartner: (state: any) => AdPartner = createSelector(
  getState,
  (state) => (state && state.selectedPartner) || null
);
export const getSelectedPartnerExtraData: (state: any) => ExtraData | null | undefined = createSelector(
  getSelectedPartner,
  (state) => {
    return (state && state.extra_data) || null;
  }
);
export const getSelectedPartnerExtraDataSnapAppIds: (state: any) => SnapAppIds = createSelector(
  getSelectedPartnerExtraData,
  (state) => {
    return (state && state.snap_ad_app_ids) || null;
  }
);
export const getSelectedPartnerExtraDataSnapData: (state: any) => SnapAppIds = createSelector(
  getSelectedPartnerExtraData,
  (state) => {
    return (state && state.snap_data) || null;
  }
);
export const getSelectedPartnerSnapEnableAdvancedConversion: (state: any) => boolean = createSelector(
  getSelectedPartnerExtraDataSnapData,
  (state) => {
    return (state && state.ios_user_data_send_enabled) || false;
  }
);
export const getSelectedPartnerExtraDataTikTokAccounts: (state: any) => TikTokAccount[] | null = createSelector(
  getSelectedPartnerExtraData,
  (state) => {
    return (state && state.tiktok_accounts) || null;
  }
);
export const getPartners: (state: any) => AdPartner[] = createSelector(getState, (state) => (state && state.partners) || []);
export const selectVisibleAdPartners: (state: any) => AdPartner[] = createSelector(getPartners, (partners) =>
  partners.filter(isAdPartnerVisible)
);
export const selectEngagementBuilderEnabledAdPartners: (state: any) => AdPartner[] = createSelector(
  selectVisibleAdPartners,
  (partners) => partners.filter((adPartner) => !ENGAGEMENT_BUILDER_PARTNER_BLACKLIST.includes(adPartner.machine_name))
);
export const isLoadingAdPartner: (state: any) => boolean = createSelector(
  getState,
  (state) => (state && state.loading) || false
);
export const hasLoadedAdPartners: (state: any) => boolean = createSelector(
  getState,
  (state) => (state && state.hasPartnersLoaded) || false
);
export const selectFacebookData: (state: any) => FacebookData | null | undefined = createSelector(
  getSelectedPartnerExtraData,
  (extraData) => {
    return extraData && extraData.facebook_data;
  }
);
export const selectFacebookLimitedDataOptions: (state: any) => string[] | null | undefined = createSelector(
  selectFacebookData,
  (facebookData) => {
    return facebookData && facebookData.limited_data_options;
  }
);
export const selectFacebookLimitedDataUse: (state: any) => boolean = createSelector(
  selectFacebookLimitedDataOptions,
  (facebookLimitedDataOptions) => {
    if (!facebookLimitedDataOptions) {
      // the default value for LIMITED_DATA_USE is true in the backend,
      // and dashboard needs to reflect this default value if no explicit
      // option is set.
      return true;
    }

    return facebookLimitedDataOptions.includes(LIMITED_DATA_USE);
  }
);
export const selectFacebookAEMEnabled: (state: any) => boolean = createSelector(selectFacebookData, (facebookData) => {
  return facebookData && !!facebookData.enable_aggregated_event_measurement;
});
export const getSelectedPartnerExtraDataRokuSettings: (state: any) => ExtraData | null | undefined = createSelector(
  getSelectedPartnerExtraData,
  (state) => {
    return (state && state.roku_settings) || RokuDefaults.Data;
  }
);
export const selectAppInheritsFromOrg: (state: AdPartner) => boolean = createSelector(getSelectedPartner, (state) => {
  const { app_id, organization_id } = state || {};
  return !!(app_id && organization_id);
});
export const getCredentialsFromSelectedPartnerOrDefault: (state: AdPartner) => any = createSelector(
  getSelectedPartner,
  (state: AdPartner) => {
    return state.credentials || {};
  }
);
export const getLoadingState: (state: any) => boolean = createSelector(getState, (state) => state.loading);

export const selectCustomPartnerLimits: (state: any) => CustomPartnerLimitResponse | null = createSelector(
  getState,
  (state) => state.customPartnerLimits
);

export const selectCustomPartnerLimitsLoading: (state: any) => boolean = createSelector(
  getState,
  (state) => state.customPartnerLimitsLoading
);

export const selectCustomPartnerLimitsLimit: (state: any) => number | undefined = createSelector(
  selectCustomPartnerLimits,
  (customPartnerLimits) => customPartnerLimits?.limit
);

export const selectCustomPartnersBasedOnView: (state: any) => CustomPartnerData[] = createSelector(
  selectCustomPartnerLimits,
  selectCustomPartnerLimitsLoading,
  isUserOnAppView,
  (customPartnerLimits, loading, isOnAppView) => {
    if (!customPartnerLimits?.customPartners || loading) return [];

    if (isOnAppView) {
      return customPartnerLimits.customPartners.filter(({ isEnabled, isOrgLevel }) => isEnabled && !isOrgLevel);
    } else {
      return customPartnerLimits.customPartners.filter(({ isEnabled, isOrgLevel }) => isEnabled && isOrgLevel);
    }
  }
);

export const isAddingNewCustomPartnerAllowed: (state: any) => boolean = createSelector(
  selectCustomPartnerLimits,
  selectCustomPartnerLimitsLoading,
  isUserOnAppView,
  (customPartnerLimits, loading, isOnAppView) => {
    if (!customPartnerLimits || !customPartnerLimits.customPartners || loading) return false;

    if (isOnAppView) {
      const appLevelPartners = customPartnerLimits.customPartners.filter(
        ({ isEnabled, isOrgLevel }) => isEnabled && !isOrgLevel
      );
      return appLevelPartners.length < customPartnerLimits.limit;
    } else {
      const orgLevelPartners = customPartnerLimits.customPartners.filter(
        ({ isEnabled, isOrgLevel }) => isEnabled && isOrgLevel
      );
      return orgLevelPartners.length < customPartnerLimits.limit;
    }
  }
);
